'use client';

import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Check } from 'lucide-react';
import Link from 'next/link';
import { features } from '@/lib/config';

const PLAN_LABELS: Record<string, string> = {
  starter: 'Starter',
  pro: 'Pro',
  premium: 'Max',
};

function sanitizePlan(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.toLowerCase();
  return v in PLAN_LABELS ? v : null;
}

function SignUpContent() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const selectedPlan = sanitizePlan(searchParams.get('plan'));
  const planLabel = selectedPlan ? PLAN_LABELS[selectedPlan] : null;

  // Redirect if already authenticated
  useEffect(() => {
    if (session?.user) {
      router.push('/builder');
    }
  }, [session, router]);

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    try {
      // Persist the picked plan across the OAuth round-trip. The callbackUrl
      // carries it through NextAuth; localStorage is a belt-and-suspenders
      // fallback the /redeem page reads if the query is ever dropped.
      if (selectedPlan) {
        try {
          localStorage.setItem('selectedPlan', selectedPlan);
        } catch {
          /* ignore */
        }
      }
      const callbackUrl = selectedPlan ? `/redeem?plan=${selectedPlan}` : '/redeem';
      await signIn('google', { callbackUrl });
    } catch (error) {
      console.error('Sign-up error:', error);
      setIsLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 dark:from-purple-950/20 dark:via-blue-950/20 dark:to-pink-950/20 flex flex-col">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <Link href="/" className="flex items-center gap-0.5 group w-fit">
            <img src="/logo-icon.png" alt="" aria-hidden className="h-10 w-auto transition-transform group-hover:scale-105" />
            <img src="/logo-wordmark.png" alt="Deckster" className="h-8 w-auto transition-transform group-hover:scale-105" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl">
              {planLabel ? `Create your account — ${planLabel} plan` : 'Create your account'}
            </CardTitle>
            <CardDescription>
              {planLabel
                ? `Sign up to activate the ${planLabel} plan and start creating with AI`
                : 'Start creating stunning presentations with AI'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Benefits List */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/30 dark:to-blue-950/30 rounded-lg p-4 space-y-3">
              <p className="font-medium text-sm">What you'll get:</p>
              <div className="space-y-2">
                {[
                  'AI-powered presentation creation',
                  'Multi-agent collaboration system',
                  'Real-time slide editing',
                  'Professional templates',
                  features.couponAuthEnabled
                    ? 'Instant access with an access code'
                    : 'Manual approval for early access',
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Sign Up Button */}
            <Button
              onClick={handleGoogleSignUp}
              disabled={isLoading}
              size="lg"
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {isLoading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating account...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#FFFFFF"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      opacity="0.9"
                    />
                    <path
                      fill="#FFFFFF"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      opacity="0.9"
                    />
                    <path
                      fill="#FFFFFF"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      opacity="0.9"
                    />
                    <path
                      fill="#FFFFFF"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      opacity="0.9"
                    />
                  </svg>
                  Sign up with Google
                </>
              )}
            </Button>

            {/* Access Notice */}
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-800 dark:text-blue-200 text-center">
                {features.couponAuthEnabled ? (
                  <><strong>Note:</strong> You&apos;ll need an access code after sign-up. Enter it to unlock your account and receive credits.</>
                ) : (
                  <><strong>Note:</strong> New accounts require manual approval. You&apos;ll be notified within 24-48 hours.</>
                )}
              </p>
            </div>

            {/* Terms */}
            <p className="text-xs text-center text-muted-foreground">
              By signing up, you agree to our{' '}
              <Link href="/terms" className="underline hover:text-foreground">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy" className="underline hover:text-foreground">
                Privacy Policy
              </Link>
            </p>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Already have an account?
                </span>
              </div>
            </div>

            {/* Sign In Link */}
            <div className="text-center">
              <Link href="/auth/signin">
                <Button variant="link" className="text-purple-600">
                  Sign in instead
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function SignUpPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    }>
      <SignUpContent />
    </Suspense>
  );
}
